import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap";
import html2canvas from "html2canvas";
import { createSupportRequest } from "../../atasksapi";
import { listPracticeUsers } from "../../api";
import MentionTextarea from "../Tasks/MentionTextarea";
import ImageAnnotator from "../Sessions/EditImages";
import { loadArticles, searchArticles } from "../../knowledgeBaseApi";

export function SupportModal({ show, onHide, onOpenHelp }) {
  const [description, setDescription] = useState("");
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [priority, setPriority] = useState("Medium");
  const [screenshot, setScreenshot] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [practiceUsers, setPracticeUsers] = useState([]);
  const [resetSignal, setResetSignal] = useState(0);
  const [helpArticles, setHelpArticles] = useState([]);
  const [helpHits, setHelpHits] = useState([]);
  const fileInputRef = useRef(null);
  const annotatorRef = useRef(null);

  const pageUrl = window.location.href;

  // Load practice users on each open so a newly enabled colleague shows up
  // without a refresh. Reuses the same endpoint as the Tasks "Create Task"
  // modal — server filters to users with features.tasks: true.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    listPracticeUsers()
      .then((users) => { if (!cancelled) setPracticeUsers(Array.isArray(users) ? users : []); })
      .catch(() => { if (!cancelled) setPracticeUsers([]); });
    return () => { cancelled = true; };
  }, [show]);

  /*
   * Ticket deflection.
   *
   * The knowledge base is searched against what the user is already typing,
   * rather than behind a second search box they would have to notice and use.
   * Somebody describing "cannot send a whatsapp template" has told us what they
   * need; asking them to phrase it again as a query is how a help centre ends
   * up unread while the tickets keep coming.
   *
   * The manifest is cached module-side, so the fetch happens at most once per
   * page load and is usually already done by the time this modal opens.
   */
  useEffect(() => {
    if (!show) return;
    loadArticles()
      .then(setHelpArticles)
      .catch(() => setHelpArticles([]));
  }, [show]);

  useEffect(() => {
    // Short enough and it matches half the library, which is noise rather than
    // help. Debounced so it does not run on every keystroke.
    if (description.trim().length < 6 || !helpArticles.length) {
      setHelpHits([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      setHelpHits(searchArticles(helpArticles, description).slice(0, 3));
    }, 300);
    return () => clearTimeout(timer);
  }, [description, helpArticles]);

  const readArticle = (articleId) => {
    // Close this modal first. The help panel is a drawer with no backdrop, and
    // opening it underneath an open modal would put the answer behind the form
    // asking the question.
    handleClose();
    onOpenHelp?.(articleId);
  };

  const resetForm = () => {
    setDescription("");
    setMentionedUserIds([]);
    setPriority("Medium");
    setScreenshot(null);
    setSuccessMsg("");
    setErrorMsg("");
    setResetSignal((n) => n + 1);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleCaptureScreenshot = async () => {
    setIsCapturing(true);
    const modalBackdrops = document.querySelectorAll(".modal-backdrop, .modal");
    modalBackdrops.forEach((el) => (el.style.visibility = "hidden"));

    try {
      await new Promise((r) => setTimeout(r, 200));
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 1,
        logging: false,
      });
      setScreenshot(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("Screenshot capture failed:", err);
      setErrorMsg("Could not capture screenshot. Try uploading one instead.");
    } finally {
      modalBackdrops.forEach((el) => (el.style.visibility = ""));
      setIsCapturing(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshot(ev.target.result);
    reader.readAsDataURL(file);
  };

  const dataURLtoBlob = (dataUrl) => {
    const [header, base64] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
    return new Blob([array], { type: mime });
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setErrorMsg("Please enter a description of the issue.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      let screenshotBlob = null;
      if (screenshot) {
        let finalScreenshot = screenshot;
        if (annotatorRef.current) {
          try {
            const merged = await annotatorRef.current.mergeImage();
            if (merged) finalScreenshot = merged;
          } catch {
            // fall back to original screenshot
          }
        }
        screenshotBlob = dataURLtoBlob(finalScreenshot);
      }

      await createSupportRequest({
        description: description.trim(),
        priority,
        pageUrl,
        screenshot: screenshotBlob,
        mentionedUserIds,
      });

      setSuccessMsg("Support request submitted successfully!");
      window.dispatchEvent(new CustomEvent('tasks:created'));
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to submit support request:", err);
      setErrorMsg("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          <i className="fa-light fa-life-ring me-2" />
          Support Request
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {successMsg && <Alert variant="success">{successMsg}</Alert>}
        {errorMsg && (
          <Alert variant="danger" dismissible onClose={() => setErrorMsg("")}>
            {errorMsg}
          </Alert>
        )}

        {helpHits.length > 0 && (
          <Alert variant="info" className="py-2 px-3">
            <div className="fw-semibold mb-1">
              <i className="fa-light fa-circle-question me-2" />
              This might already be answered
            </div>
            {helpHits.map((a) => (
              <Button
                key={a.id}
                variant="link"
                className="d-block text-start p-0 text-decoration-none"
                onClick={() => readArticle(a.id)}
              >
                {a.title}
              </Button>
            ))}
          </Alert>
        )}

        <Form>
          <Row className="mb-2">
            <Col xs={9} className="d-flex flex-column">
              <Form.Label className="fw-semibold">
                Describe the issue <span className="text-danger">*</span>
                <span className="text-muted small ms-2">(type @ to notify a colleague)</span>
              </Form.Label>
              <MentionTextarea
                value={description}
                onChange={({ text, mentionedUserIds: ids }) => {
                  setDescription(text);
                  setMentionedUserIds(ids || []);
                }}
                practiceUsers={practiceUsers}
                placeholder="Please describe what happened, what you expected, and any steps to reproduce..."
                rows={8}
                disabled={isSubmitting}
                resetSignal={resetSignal}
                textareaStyle={{ minHeight: "12rem", resize: "vertical" }}
              />
            </Col>
            <Col xs={3} className={"d-flex flex-column gap-2" + (isSubmitting ? " disabled" : "")}>
              <Form.Label className="fw-semibold">Priority</Form.Label>
              <div className="d-flex flex-column gap-2">
                {["Highest", "High", "Medium", "Low"].map((p) => (
                  <Form.Check
                    key={p}
                    type="radio"
                    id={`priority-${p}`}
                    name="priority"
                    label={<span>{p}</span>}
                    checked={priority === p}
                    onChange={() => setPriority(p)}
                    disabled={isSubmitting}
                  />
                ))}
              </div>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Screenshot</Form.Label>
            <div className="d-flex gap-2 mb-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleCaptureScreenshot}
                disabled={isCapturing || isSubmitting}
              >
                {isCapturing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Capturing...
                  </>
                ) : (
                  <>
                    <i className="fa-light fa-camera me-1" />
                    Capture Screenshot
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                <i className="fa-light fa-upload me-1" />
                Upload Image
              </Button>
              {screenshot && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setScreenshot(null)}
                  disabled={isSubmitting}
                >
                  <i className="fa-light fa-trash me-1" />
                  Remove
                </Button>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            {screenshot && (
              <div className="border rounded p-2">
                <ImageAnnotator
                  ref={annotatorRef}
                  imageUrl={screenshot}
                  onUpdateImage={setScreenshot}
                  maxWidth={680}
                  maxHeight={380}
                />
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex gap-1 text-center">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{ minWidth: "120px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !description.trim()}
            style={{ minWidth: "120px" }}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Submitting...
              </>
            ) : (
              <>
                <i className="fa-light fa-paper-plane me-2" />
                Send Request
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
